import React, { useRef, useEffect, useState } from 'react';
// import css for ol
import 'ol/ol.css';
import './ol-extra.css';

import { Modify, Select, Translate } from 'ol/interaction';
import { click } from 'ol/events/condition';

import { Grid2, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { useTranslation } from 'react-i18next';
import { SVG_MODELS, SVG_WRAPPERS } from './SvgLibrary.js';
import {
    zoomToFloorplanExtent,
    reverseScale,
    makeStyleForItem,
    createButtonControl,
    createMapWithControls,
    zoomChange,
    featureFromItem,
    createVectorLayerAndSource,
    snapToGrid,
    ROTATE_STEP,
} from './mapFunctions.js';

const defaultAddedElements = [
    {
        id: 1,
        name: 'Chair',
        nameI18n: {},
        bookable: true,
        svg: SVG_MODELS.chair_1,
        x: 200,
        y: 200,
        rotation: 180, // changed to 180 degree rotation
        scale: 1,
        model: 'chair_1',
    },
    {
        id: 2,
        name: 'Vip Chair',
        nameI18n: {},
        bookable: true,
        svg: SVG_MODELS.vip_chair_1,
        x: 200,
        y: 200,
        rotation: 180, // changed to 180 degree rotation
        scale: 1,
        model: 'vip_chair_1',
    },
    {
        id: 3,
        name: 'Table',
        nameI18n: {},
        bookable: false,
        svg: SVG_MODELS.table_1,
        x: 250,
        y: 250,
        rotation: 0,
        scale: 1,
        model: 'table_1',
    },
    {
        id: 4,
        name: 'Table 2',
        nameI18n: {},
        bookable: false,
        svg: SVG_MODELS.table_2,
        x: 250,
        y: 250,
        rotation: 0,
        scale: 1,
        model: 'table_2',
    },
    {
        id: 5,
        name: 'Table 3',
        nameI18n: {},
        bookable: false,
        svg: SVG_MODELS.table_3,
        x: 250,
        y: 250,
        rotation: 0,
        scale: 1,
        model: 'table_3',
    },
    {
        id: 6,
        name: 'Table 4',
        nameI18n: {},
        bookable: false,
        svg: SVG_MODELS.table_4,
        x: 250,
        y: 250,
        rotation: 0,
        scale: 1,
        model: 'table_4',
    },
    {
        id: 7,
        name: 'Table 5',
        nameI18n: {},
        bookable: false,
        svg: SVG_MODELS.table_5,
        x: 250,
        y: 250,
        rotation: 0,
        scale: 1,
        model: 'table_5',
    },
    {
        id: 8,
        name: 'Table 6',
        nameI18n: {},
        bookable: false,
        svg: SVG_MODELS.table_6,
        x: 250,
        y: 250,
        rotation: 0,
        scale: 1,
        model: 'table_6',
    },
    {
        id: 9,
        name: 'Stage',
        nameI18n: {},
        bookable: false,
        svg: SVG_MODELS.stage,
        x: 250,
        y: 250,
        rotation: 0,
        scale: 1,
        model: 'stage',
    },
];

const ROTATE_OPTIONS = [-45, 45];

/*
const handleFileUpload = (e, setSvg) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
        setSvg(evt.target.result);
    };
    reader.readAsText(file);
};*/

function SvgEditor({
    elements,
    addElement,
    modifyElement,
    selectedElementId,
    setSelectedElementId,
    deleteSeat,
    mode = 'regular',
    unplacedElements = [],
}) {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const vectorSourceRef = useRef(null);
    const firstTimeElementsSetRef = useRef(false);
    const featureMapRef = useRef([]);
    const resizeObserverRef = useRef(null);
    const modifyElementRef = useRef(modifyElement);
    useEffect(() => {
        modifyElementRef.current = modifyElement;
    }, [modifyElement]);

    const { t } = useTranslation();
    const lang = t('common.language'); // Get the current language from i18n

    const [showLabels, setShowLabels] = useState(true);
    const selectedElement = elements.find((el) => el.id === selectedElementId);

    // Initialize OpenLayers map
    useEffect(() => {
        if (!mapRef.current) return;

        const { vectorSource, vectorLayer } = createVectorLayerAndSource();
        vectorSourceRef.current = vectorSource;

        const fitControl = createButtonControl({
            label: '&#x2922;',
            title: 'Fit to floorplan',
            onClick: () => zoomToFloorplanExtent(map, vectorSourceRef.current),
        });

        const zoomInControl = createButtonControl({
            label: '+',
            title: 'Zoom In',
            onClick: () => zoomChange(mapInstanceRef.current.getView(), 1),
        });

        const zoomOutControl = createButtonControl({
            label: '–',
            title: 'Zoom Out',
            onClick: () => zoomChange(mapInstanceRef.current.getView(), -1),
        });

        const labelToggleControl = createButtonControl({
            label: '&#9872  ',
            title: 'Toggle Labels',
            onClick: () => setShowLabels((prev) => !prev),
        });

        console.log('Initializing OpenLayers map');
        const map = createMapWithControls(mapRef, vectorLayer, [
            fitControl,
            zoomInControl,
            zoomOutControl,
            labelToggleControl,
        ]);

        // Select interaction
        const selectInteraction = new Select({
            condition: click,
            layers: [vectorLayer],
            hitTolerance: 30,
            multi: false,
            style: (feature) => {
                return makeStyleForItem(feature.originalItem, map.getView().getZoom(), lang);
            },
        });

        const featureDrager = new Translate();
        // Modify interaction for dragging
        const modifyInteraction = new Modify({
            features: selectInteraction.getFeatures(),
            multi: false,
        });

        featureDrager.on('translateend', (event) => {
            event.features.forEach((feature) => {
                const itemId = feature.get('itemId');
                const geometry = feature.getGeometry();
                const coordinates = geometry.getCoordinates();
                console.log('Feature dragged:', itemId, coordinates);
                modifyElementRef.current((e) => {
                    return snapToGrid({ ...e, x: coordinates[0], y: coordinates[1] });
                }, itemId);
            });
        });
        modifyInteraction.on('modifyend', (event) => {
            event.features.forEach((feature) => {
                const itemId = feature.get('itemId');
                const geometry = feature.getGeometry();
                const coordinates = geometry.getCoordinates();
                console.log('Feature modified:', itemId, coordinates);
                modifyElementRef.current((e) => {
                    return snapToGrid({ ...e, x: coordinates[0], y: coordinates[1] });
                }, itemId);
            });
        });

        map.addInteraction(featureDrager);
        map.addInteraction(modifyInteraction);

        // Handle selection changes
        let internalSelectedElementId = null;
        selectInteraction.on('select', (event) => {
            if (event.selected.length > 0) {
                const feature = event.selected[0];
                const id = feature.getId();
                setSelectedElementId(id);
                internalSelectedElementId = id;
            }
            // prevent deselection
            else {
                setSelectedElementId(null);
                internalSelectedElementId = null;
            }
        });

        // Handle mouse wheel for rotation (Shift + Scroll)
        map.getViewport().addEventListener('wheel', (event) => {
            if (event.shiftKey && internalSelectedElementId) {
                event.preventDefault();
                event.stopPropagation();

                // Determine rotation direction based on scroll direction
                const rotationChange = event.deltaX > 0 ? ROTATE_STEP : -ROTATE_STEP;

                modifyElementRef.current((e) => {
                    // the original mapping needs to be preserved  (item.rotation * Math.PI) / 180 in the one
                    return snapToGrid({ ...e, rotation: (e.rotation + rotationChange) % 360 });
                }, internalSelectedElementId);
                return false; // Prevent default scrolling behavior
            }
        });

        // Update icon scales dynamically when zoom changes
        map.getView().on('change:resolution', () => {
            const zoom = map.getView().getZoom();
            featureMapRef.current.forEach((feature) => {
                const currentStyle = feature.getStyle();
                const image = currentStyle.getImage();
                if (image) {
                    image.setScale(reverseScale(zoom, feature.originalItem.scale));
                    feature.setStyle(currentStyle);
                }
            });
        });

        map.addInteraction(selectInteraction);
        mapInstanceRef.current = map;

        // Set up ResizeObserver to handle container resize
        if (mapRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.updateSize();
                }
            });
            resizeObserverRef.current.observe(mapRef.current);
        }

        return () => {
            map.setTarget(undefined);
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };
    }, [mapRef, setSelectedElementId, lang]);

    // Re-fit view when switching between regular/indoor mode
    useEffect(() => {
        if (!mapInstanceRef.current || !vectorSourceRef.current) return;
        setTimeout(() => {
            zoomToFloorplanExtent(mapInstanceRef.current, vectorSourceRef.current);
        }, 50);
    }, [mode]);

    // Update features when items change
    useEffect(() => {
        if (!mapInstanceRef.current || !vectorSourceRef.current) return;
        if (!mapInstanceRef.current?.getView()) {
            console.warn('Map view is not initialized');
            return;
        }
        featureMapRef.current = [];
        vectorSourceRef.current.clear();

        const zoom = mapInstanceRef.current.getView().getZoom();

        elements.forEach((item) => {
            //console.log('Adding feature for item:', item);
            const f = featureFromItem(item, lang, zoom, false, {}, !showLabels); // last param: remove labels flag
            vectorSourceRef.current.addFeature(f);
            featureMapRef.current.push(f);
        });
        // if it's the first time, zoom to the extent of the features
        if (mapInstanceRef.current && elements.length > 0 && !firstTimeElementsSetRef.current) {
            firstTimeElementsSetRef.current = true;
            console.log('Zooming to floorplan extent for the first time');
            setTimeout(() => {
                zoomToFloorplanExtent(mapInstanceRef.current, vectorSourceRef.current);
            }, 200);
        }
    }, [elements, lang, showLabels]);

    return (
        <div style={{ paddingBottom: '5rem', width: '100%' }}>
            <div
                style={{
                    position: 'relative',
                    border: '1px solid #ccc',
                    background: '#f8f9fa',
                    width: '100%',
                    margin: '0 auto',
                }}
            >
                <div ref={mapRef} style={{ background: '#f8f9fa', width: '100%', height: 600 }} />
                <div style={{ width: '100%', margin: '0 auto' }}>
                    <div
                        style={{
                            border: '1px solid #ccc',
                            paddingTop: '.5rem',
                            marginTop: '-8px',
                            borderTop: 'none',
                            background: 'white',
                        }}
                    >
                        <Grid2 container>
                            <Grid2
                                style={{
                                    maxHeight: 120,
                                    overflowY: 'auto',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                }}
                            >
                                {mode === 'regular'
                                    ? defaultAddedElements.map((el) => (
                                          <IconButton
                                              key={el.id}
                                              onClick={() => addElement(el)}
                                              variant="text"
                                          >
                                              <span
                                                  dangerouslySetInnerHTML={{
                                                      __html: SVG_WRAPPERS[el.model].replace(
                                                          '{{SVG_CONTENT}}',
                                                          el.svg ||
                                                              SVG_MODELS[el.model] ||
                                                              SVG_MODELS['chair_1'] ||
                                                              SVG_MODELS['vip_chair_1'],
                                                      ),
                                                  }}
                                                  style={{ zoom: 0.2 }}
                                              />
                                          </IconButton>
                                      ))
                                    : unplacedElements.map((el) => (
                                          <Tooltip key={el.id} title={el.name}>
                                              <IconButton
                                                  onClick={() => addElement(el)}
                                                  variant="text"
                                              >
                                                  <span
                                                      dangerouslySetInnerHTML={{
                                                          __html: (
                                                              SVG_WRAPPERS[el.model] ||
                                                              SVG_WRAPPERS['chair_1']
                                                          ).replace(
                                                              '{{SVG_CONTENT}}',
                                                              el.svg ||
                                                                  SVG_MODELS[el.model] ||
                                                                  SVG_MODELS['chair_1'],
                                                          ),
                                                      }}
                                                      style={{ zoom: 0.2 }}
                                                  />
                                              </IconButton>
                                          </Tooltip>
                                      ))}
                            </Grid2>
                            <Grid2 style={{ flex: 1 }}>
                                {selectedElement && (
                                    <>
                                        {mode === 'regular' && (
                                            <>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedElement.bookable}
                                                    onChange={(e) => {
                                                        const newBookable = e.target.checked;
                                                        modifyElement((e) => {
                                                            return { ...e, bookable: newBookable };
                                                        }, selectedElementId);
                                                    }}
                                                />
                                                <input
                                                    type="text"
                                                    value={selectedElement.name}
                                                    onChange={(e) => {
                                                        const newName = e.target.value;
                                                        modifyElement((e) => {
                                                            return { ...e, name: newName };
                                                        }, selectedElementId);
                                                    }}
                                                />
                                            </>
                                        )}
                                        <input
                                            type="number"
                                            value={selectedElement.scale}
                                            onChange={(e) => {
                                                const newScale = parseFloat(e.target.value);
                                                modifyElement((e) => {
                                                    return { ...e, scale: newScale };
                                                }, selectedElementId);
                                            }}
                                            step={0.25}
                                            style={{ width: 45 }}
                                        />
                                        {ROTATE_OPTIONS.map((option) => (
                                            <IconButton
                                                key={option}
                                                size="small"
                                                onClick={() => {
                                                    const newRotation =
                                                        (selectedElement.rotation + option) % 360;
                                                    modifyElement((e) => {
                                                        return snapToGrid({
                                                            ...e,
                                                            rotation: newRotation,
                                                        });
                                                    }, selectedElementId);
                                                }}
                                            >
                                                <Tooltip title={`${option}°`}>
                                                    {option > 0 ? (
                                                        <RotateRightIcon />
                                                    ) : (
                                                        <RotateLeftIcon />
                                                    )}
                                                </Tooltip>
                                            </IconButton>
                                        ))}
                                        <IconButton onClick={() => deleteSeat(selectedElementId)}>
                                            <Tooltip
                                                title={t(
                                                    mode === 'regular'
                                                        ? 'common.delete'
                                                        : 'seats.removeFromArrangement',
                                                )}
                                            >
                                                <DeleteIcon />
                                            </Tooltip>
                                        </IconButton>
                                    </>
                                )}
                            </Grid2>
                        </Grid2>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default React.memo(SvgEditor);
